import React from "react";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import Typography from "@material-ui/core/Typography";
import IconButton from "components/icon-button";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { DataGridItem } from "components/data-grid";
import { StyledTextField } from "components/form/form-textfield";
import withStyles from "components/with-styles";
import styles from "./styles/FileConfigurationSection.css.js";
import { useCurrentCustomerContext } from "../../../../../components/current-customer-context";
import { useParams } from "react-router-dom";

const FileConfigurationSection = ({ intl, values, errors, setValue, classes }) => {
  const { contractId } = useParams();

  const { contracts } = useCurrentCustomerContext();

  const deploymentId = contracts?.find(contract => contract._id === contractId)?.deployment_id;

  const handleFileFieldChange = fieldName => e => {
    const { value } = e.target;
    setValue(`file.${fieldName}`, value);
  };

  const handleAddFacilityChange = e => {
    const isChecked = e.target.checked;
    if (isChecked && deploymentId) {
      setValue("file.deployment_id", deploymentId);
    } else {
      setValue("file.deployment_id", undefined);
    }
  };

  const handleDnsChange = index => e => {
    const { value } = e.target;
    const newDnsNames = [...(values.file?.set_dns_names || [])];
    newDnsNames[index] = value;
    setValue("file.set_dns_names", newDnsNames);
  };

  const addDnsField = () => {
    const currentDnsNames = values.file?.set_dns_names || [];
    setValue("file.set_dns_names", [...currentDnsNames, ""]);
  };

  const removeDnsField = index => () => {
    const newDnsNames = [...(values.file?.set_dns_names || [])];
    newDnsNames.splice(index, 1);
    setValue("file.set_dns_names", newDnsNames);
  };

  return (
    <>
      <DataGridItem>
        <StyledTextField
          type="text"
          id="file.name"
          name="file.name"
          value={values.file?.name || ""}
          onChange={handleFileFieldChange("name")}
          label={intl.formatMessage({ id: "roomTemplate.file.name" }, { defaultMessage: "Name" })}
          error={!!errors.file?.name}
          helperText={errors.file?.name?.message}
        />
      </DataGridItem>
      <DataGridItem big>
        <Typography variant="subtitle1" gutterBottom>
          {intl.formatMessage({ id: "roomTemplate.file.set_dns_names" }, { defaultMessage: "DNS Names" })}
        </Typography>
        <div className={classes.dnsGridContainer}>
          {(values.file?.set_dns_names || [""]).map((dnsName, index) => (
            <div key={index} className={classes.dnsGridItem}>
              <StyledTextField
                type="text"
                id={`file.set_dns_names.${index}`}
                name={`file.set_dns_names.${index}`}
                value={dnsName || ""}
                onChange={handleDnsChange(index)}
                label={intl.formatMessage(
                  {
                    id: "roomTemplate.file.dns.item",
                    defaultMessage: `DNS Name {index}`,
                  },
                  {
                    index: index + 1,
                  },
                )}
                error={!!errors.file?.set_dns_names?.[index]}
                helperText={errors.file?.set_dns_names?.[index]?.message}
                fullWidth
              />
              {(values.file?.set_dns_names || []).length > 1 && (
                <IconButton
                  color="secondary"
                  onClick={removeDnsField(index)}
                  size="small"
                  aria-label="remove dns"
                  className={classes.deleteButton}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </div>
          ))}
        </div>
        <div className={classes.addButtonContainer}>
          <IconButton color="primary" onClick={addDnsField} size="small" aria-label="add dns">
            <AddIcon />
          </IconButton>
        </div>
      </DataGridItem>
      <DataGridItem>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!values.file?.deployment_id}
              onChange={handleAddFacilityChange}
              name="file.add_facility"
              color="primary"
              disabled={!deploymentId}
            />
          }
          label={intl.formatMessage({ id: "roomTemplate.file.addFacility" }, { defaultMessage: "Add facility" })}
        />
        {!deploymentId && (
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: "4px" }}>
            {intl.formatMessage(
              { id: "roomTemplate.file.addFacility.noDeployment" },
              { defaultMessage: "No facility associated with this contract" },
            )}
          </Typography>
        )}
      </DataGridItem>
    </>
  );
};

FileConfigurationSection.propTypes = {
  intl: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

export default injectIntl(withStyles(styles)(FileConfigurationSection));
